import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import {
  useRequestPhoneUpdateOtp,
  useVerifyPhoneUpdateOtp,
} from "@/features/auth/services";
import {
  getPhoneVerificationState,
  updatePhoneVerificationState,
} from "@/shared/utils/phoneVerification";
import Button from "../../../shared/components/Button";
import LinearLayout from "../../../shared/components/LinearLayout";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "../../../shared/components/ui/input-otp";

const CODE_LENGTH = 6;

const getRedirect = (value?: string | null) =>
  value && value.length > 0 ? value : null;

export default function PhoneVerificationCodeForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const locationState =
    (location.state as { phoneNumber?: string; redirectTo?: string } | null) ??
    null;
  const storedPhoneVerification = getPhoneVerificationState();
  const phoneNumber =
    locationState?.phoneNumber ??
    searchParams.get("phone") ??
    storedPhoneVerification.phoneNumber ??
    "";
  const normalizedPhoneNumber = phoneNumber.trim();
  const redirectTo =
    getRedirect(locationState?.redirectTo) ??
    getRedirect(searchParams.get("redirectTo")) ??
    "/dashboard";
  const [verificationCode, setVerificationCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync: verifyPhoneOtp, isPending } = useVerifyPhoneUpdateOtp();
  const { mutateAsync: resendPhoneOtp, isPending: isResending } =
    useRequestPhoneUpdateOtp();
  const queryClient = useQueryClient();

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!normalizedPhoneNumber) {
      setError("Missing phone number. Please request a new code.");
      return;
    }

    if (verificationCode.trim().length !== CODE_LENGTH) {
      setError("Enter the 6-digit verification code.");
      return;
    }

    try {
      await verifyPhoneOtp({
        phoneNumber: normalizedPhoneNumber,
        otp: verificationCode.trim(),
      });
      updatePhoneVerificationState({
        phoneNumber: normalizedPhoneNumber,
        verified: true,
      });
      await queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast.success("Phone number verified successfully.");

      navigate(redirectTo, { replace: true });
    } catch (submissionError) {
      const message =
        submissionError instanceof Error
          ? submissionError.message
          : "Unable to verify the code. Please try again.";
      toast.error(message);
      setError(message);
    }
  };

  const handleResend = async () => {
    if (!normalizedPhoneNumber) {
      setError("Missing phone number. Please request a new code.");
      return;
    }

    try {
      await resendPhoneOtp({ phoneNumber: normalizedPhoneNumber });
      updatePhoneVerificationState({
        phoneNumber: normalizedPhoneNumber,
        verified: false,
      });
      toast.success("A new verification code has been sent to your phone.");
    } catch (submissionError) {
      const message =
        submissionError instanceof Error
          ? submissionError.message
          : "Unable to resend the code. Please try again.";
      toast.error(message);
      setError(message);
    }
  };

  const maskedPhone = useMemo(() => {
    if (!normalizedPhoneNumber) return null;
    if (normalizedPhoneNumber.length <= 4) {
      const maskedLength = Math.max(normalizedPhoneNumber.length - 2, 0);
      const visibleDigits = normalizedPhoneNumber.slice(-2);
      return `${"*".repeat(maskedLength)}${visibleDigits}`;
    }

    const visibleDigits = normalizedPhoneNumber.slice(-4);
    const maskedLength = Math.max(normalizedPhoneNumber.length - 4, 4);
    return `${"*".repeat(maskedLength)}${visibleDigits}`;
  }, [normalizedPhoneNumber]);

  if (!normalizedPhoneNumber) {
    return (
      <div className="w-full">
        <LinearLayout orientation="vertical">
          <div className="text-center align-middle mx-auto space-y-5">
            <h1 className="text-white text-[20px] leading-[24px] font-medium">
              Phone Verification
            </h1>
            <p className="text-white text-sm font-sm opacity-70">
              We could not find the phone number for this verification flow.
              Please start again to request a new code.
            </p>
            <Button
              variant="primary"
              fluid
              onClick={() => {
                const params = new URLSearchParams();
                if (redirectTo) {
                  params.set("redirectTo", redirectTo);
                }
                navigate(
                  params.toString()
                    ? `/verify-phone?${params.toString()}`
                    : "/verify-phone"
                );
              }}>
              Go Back
            </Button>
          </div>
        </LinearLayout>
      </div>
    );
  }

  return (
    <div className="w-full">
      <LinearLayout orientation="vertical">
        <form
          onSubmit={handleSubmit}
          className="text-center align-middle mx-auto space-y-4">
          <h1 className="text-white text-[20px] leading-[24px] font-medium">
            Phone Verification
          </h1>
          <p className="text-white text-sm font-sm opacity-50">
            Enter the code sent to
            <span className="font-semibold text-white"> {maskedPhone}</span>
          </p>
          <InputOTP
            maxLength={CODE_LENGTH}
            value={verificationCode}
            onChange={(value) => {
              setVerificationCode(value);
              setError(null);
            }}
            containerClassName="justify-center"
            className="text-white">
            <InputOTPGroup className="gap-3">
              {Array.from({ length: CODE_LENGTH }).map((_, index) => (
                <InputOTPSlot
                  key={index}
                  index={index}
                  className="bg-transparent rounded-lg! border text-lg text-white size-10"
                />
              ))}
            </InputOTPGroup>
          </InputOTP>
          {error && <p className="text-danger text-sm text-center">{error}</p>}
          <div className="mb-6">
            <button
              type="button"
              onClick={handleResend}
              disabled={isResending}
              className="underline text-[14px] text-white font-sm disabled:opacity-60">
              {isResending ? "Resending..." : "Resend Code"}
            </button>
          </div>
          <Button
            variant="primary"
            fluid
            type="submit"
            loading={isPending}
            disabled={verificationCode.trim().length !== CODE_LENGTH}>
            {isPending ? "Verifying..." : "Verify Code"}
          </Button>
        </form>
      </LinearLayout>
    </div>
  );
}
